/**
 * @file: maintenance-plan.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { HttpClient } from '@euler/lib/request';
import {
  MaintenancePlanSearchParams,
  UpdateMaintenancePlanDetailRequest,
} from '@euler/model';
import {
  AppendCustomMaintenancePlanRequest,
  MaintenancePlan,
  MaintenancePlanDetail,
  MaintenancePlanDetailListItem,
} from '@euler/model/task-detail';

export class MaintenancePlanService {
  constructor(private readonly api: HttpClient) {}

  async search(
    params: MaintenancePlanSearchParams,
    offset: number,
    limit: number,
  ): Promise<MaintenancePlan[]> {
    return await this.api
      .post()
      .url('/maintenance-plans/search')
      .data({ params, offset, limit })
      .future();
  }

  // Flat, detail-centric search for the maintenance-plans timeline: rows
  // ordered by order then detail-creation time, each carrying its denormalized
  // order/vehicle columns + joined lead coverage. The client groups by orderNo.
  async searchDetails(
    params: MaintenancePlanSearchParams,
    offset: number,
    limit: number,
  ): Promise<MaintenancePlanDetailListItem[]> {
    return await this.api
      .post()
      .url('/maintenance-plans/search-details')
      .data({ params, offset, limit })
      .future();
  }

  async appendCustomMaintenancePlan(
    request: AppendCustomMaintenancePlanRequest,
  ): Promise<MaintenancePlan> {
    return await this.api
      .post()
      .url('/maintenance-plans/append-custom-plan')
      .data(request)
      .future();
  }

  async updateMaintenancePlanDetailAlertSettings(
    planNo: string,
    detailId: number,
    alertSettings: {
      enabled?: boolean;
      enabledForCustomer?: boolean;
    },
  ): Promise<void> {
    await this.api
      .patch()
      .url(`/maintenance-plans/:planNo/details/:detailId/alert-settings`, {
        planNo,
        detailId,
      })
      .data({ alertSettings })
      .future();
  }

  async updateMaintenancePlanDetail(
    planNo: string,
    detailId: number,
    updateRequest: UpdateMaintenancePlanDetailRequest,
  ): Promise<MaintenancePlanDetail> {
    return await this.api
      .put()
      .url(`/maintenance-plans/:planNo/details/:detailId`, {
        planNo,
        detailId,
      })
      .data(updateRequest)
      .future();
  }

  async removeMaintenancePlanDetail(
    planNo: string,
    detailId: number,
  ): Promise<void> {
    await this.api
      .delete()
      .url(`/maintenance-plans/:planNo/details/:detailId`, {
        planNo,
        detailId,
      })
      .future();
  }
}
